import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next'
import { useQuery } from '@apollo/client'

import AuctionLayout from 'components/auction'
import Menu from 'components/event/menu'
import Info from 'components/event/info'
import SideMenu from 'components/event/side-menu'
import TitleDesctiption from 'components/event/title'
import EventPageContextProvider from 'components/contexts/event-page'
import EventPageFiltersContextProvider from 'components/contexts/event-page-filters'
import { getIdFromSlug } from 'utils'
import { ArtworkSalesAs, GetEventAuctionByIdQuery } from 'gql'
import { useParseJSON, useQueryParams, useScreenClass } from 'hooks'
import { Layout } from '~ui'
import { addApolloState, initializeApollo } from '~services/apollo'
import { ArtistMeta } from 'types/artist'

import styles from '~styles/pages/Event.module.scss'
import { GET_EVENT_AUCTION_BY_ID } from 'gql/artwork/queries'

export const getServerSideProps: GetServerSideProps = async ({ query, ...rest }) => {
  const apollo = initializeApollo()

  const { data, error } = await apollo.query<GetEventAuctionByIdQuery>({
    query: GET_EVENT_AUCTION_BY_ID,
    variables: {
      pk: getIdFromSlug(query.slug as string),
    },
  })

  const dayAfterEvent = new Date(data.findArtwork.eventStartDate).getTime() + 86400000
  const isExpired = new Date(dayAfterEvent) < new Date()
  const isBuyNow = data.findArtwork.salesAs === ArtworkSalesAs.BuyItNowOption

  if (isExpired && isBuyNow) {
    return {
      redirect: {
        permanent: true,
        destination: `/stars/${data.findArtwork.mainArtist.slug}`,
      },
    }
  }
  const res = {
    notFound: error || !data.findArtwork,
    props: {
      isPrivate: data.findArtwork.status === 'private',
    },
  }

  if (
    !query.vip &&
    !data.findArtwork.tevoId &&
    data.findArtwork.packageInfo.some((p) => !p.isTicket)
  ) {
    Object.assign(res, {
      redirect: {
        permanent: false,
        destination: `/experiences/${rest.params.slug}?vip=true`,
      },
    })
  }

  return addApolloState(apollo, res)
}

const EventAuctionPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = () => {
  const {
    query: { slug },
  } = useQueryParams()

  const { isMobile, isDesktop } = useScreenClass()
  const {
    data: { findArtwork },
    loading,
  } = useQuery<GetEventAuctionByIdQuery>(GET_EVENT_AUCTION_BY_ID, {
    variables: {
      pk: getIdFromSlug(slug),
    },
  })

  const artistMeta = useParseJSON<ArtistMeta>(findArtwork.mainArtist?.meta)

  if (findArtwork?.salesAs === 'AUCTION' || findArtwork?.salesAs === 'GIVEAWAY') {
    return (
      <AuctionLayout salesAs={findArtwork?.salesAs} artworkNode={findArtwork} loading={loading} />
    )
  }

  // Events page
  return (
    <div className={styles.wrapper}>
      <div style={{ gridArea: 'banner' }}>
        <Layout.Banner />
      </div>
      <div style={{ gridArea: 'header' }}>
        <Layout.Header>{isDesktop && <TitleDesctiption findArtwork={findArtwork} />}</Layout.Header>
      </div>
      <EventPageFiltersContextProvider fetchTEvoTickets={!artistMeta?.hideTEvoTickets}>
        <EventPageContextProvider>
          <Info event={findArtwork} artistMeta={artistMeta} />
          {isMobile && <TitleDesctiption findArtwork={findArtwork} />}
          <Menu
            tevoId={findArtwork.tevoId}
            packageInfo={findArtwork.packageInfo}
            eventExtendedStatus={findArtwork.eventExtendedStatus}
          />
          <SideMenu event={findArtwork} artistMeta={artistMeta} />
        </EventPageContextProvider>
      </EventPageFiltersContextProvider>
    </div>
  )
}

export default EventAuctionPage
